import React, { useState } from 'react';
import { Container, Table, Button, Card, Modal, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import PageTitle from '@/common/PageTitle/PageTitle';
import styles from './installer-vendor-clients.module.scss';

interface InstallerVendorClient {
  id: string;
  companyName: string;
  companyAddress: string;
  companyNumber: string;
  clientRole: string;
  clientPermissions: string[];
  assignedLocations: string[];
  masterAccountId: string;
  masterAccountUsers: string[];
  masterAccountRoles: string[];
}

const InstallerVendorClients: React.FC = () => {
  const { vendorNumber } = useParams<{ vendorNumber: string }>();
  const navigate = useNavigate();
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<InstallerVendorClient | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState<string>('');
  const [showRemoveClientModal, setShowRemoveClientModal] = useState(false);
  const [clientToRemove, setClientToRemove] = useState<InstallerVendorClient | null>(null);
  const [showMasterAccountUsersModal, setShowMasterAccountUsersModal] = useState(false);
  const [showMasterAccountRolesModal, setShowMasterAccountRolesModal] = useState(false);
  const [selectedClientForMasterAccount, setSelectedClientForMasterAccount] = useState<InstallerVendorClient | null>(null);

  // Mock data for vendor lookup
  const getVendorName = (vendorNumber: string) => {
    const vendors: { [key: string]: string } = {
      '555-200-0001': 'TechPro Installations',
      '555-200-0002': 'Audio Systems Plus',
      '555-200-0003': 'Digital Install Solutions',
      '555-200-0004': 'Smart Tech Installers',
      '555-200-0005': 'Pro Audio Installations'
    };
    return vendors[vendorNumber] || 'Unknown Vendor';
  };

  // Mock data for vendor clients
  const getVendorClients = (vendorNumber: string): InstallerVendorClient[] => {
    const clientsByVendor: { [key: string]: InstallerVendorClient[] } = {
      '555-200-0001': [
        {
          id: '1',
          companyName: 'Harbor Foods Corporation',
          companyAddress: '110 N Carpenter St, Chicago, IL 60607',
          companyNumber: '555-001-0001',
          clientRole: 'Admin',
          clientPermissions: ['View All', 'Edit All', 'Delete All', 'Manage Users'],
          assignedLocations: ['All Locations'],
          masterAccountId: 'MA-001',
          masterAccountUsers: ['John Smith', 'Jane Doe', 'Mike Johnson'],
          masterAccountRoles: ['Super Admin', 'Admin', 'Manager']
        },
        {
          id: '2',
          companyName: 'Bluebird Diner Group',
          companyAddress: '42 Diner Lane, Fableton, OH 43000',
          companyNumber: '555-001-0002',
          clientRole: 'Manager',
          clientPermissions: ['View Limited', 'Edit Limited'],
          assignedLocations: ['Bluebird Diner Downtown', 'Bluebird Diner Uptown'],
          masterAccountId: 'MA-002',
          masterAccountUsers: ['Sarah Wilson', 'Tom Brown'],
          masterAccountRoles: ['Manager', 'User']
        }
      ],
      '555-200-0002': [
        {
          id: '3',
          companyName: 'Summit Diners Corporation',
          companyAddress: '5505 Blue Lagoon Dr, Miami, FL 33126',
          companyNumber: '555-001-0003',
          clientRole: 'Admin',
          clientPermissions: ['View All', 'Edit All', 'Delete All', 'Manage Users'],
          assignedLocations: ['All Locations'],
          masterAccountId: 'MA-003',
          masterAccountUsers: ['Alex Rodriguez', 'Maria Garcia'],
          masterAccountRoles: ['Super Admin', 'Admin']
        }
      ],
      '555-200-0003': [
        {
          id: '4',
          companyName: 'Acme Coffee Co.',
          companyAddress: '100 Roastery Way, Beanville, WA 98000',
          companyNumber: '555-001-0004',
          clientRole: 'Manager',
          clientPermissions: ['View Limited', 'Edit Limited'],
          assignedLocations: ['Acme Coffee Downtown', 'Acme Coffee Mall'],
          masterAccountId: 'MA-004',
          masterAccountUsers: ['Lisa Chen', 'David Kim'],
          masterAccountRoles: ['Manager', 'User']
        }
      ]
    };
    return clientsByVendor[vendorNumber] || [];
  };

  console.log('Vendor Number from URL:', vendorNumber); // Will be used for API calls
  const vendorName = getVendorName(vendorNumber || '');
  const vendorClients = getVendorClients(vendorNumber || '');

  const handleAction = (action: string, client: InstallerVendorClient) => {
    switch (action) {
      case 'manage-permissions':
        setSelectedClient(client);
        setSelectedRole(client.clientRole);
        setSelectedPermissions([...client.clientPermissions]);
        setSelectedLocations([...client.assignedLocations]);
        setShowPermissionsModal(true);
        break;
      case 'view-master-account-users':
        setSelectedClientForMasterAccount(client);
        setShowMasterAccountUsersModal(true);
        break;
      case 'view-master-account-roles':
        setSelectedClientForMasterAccount(client);
        setShowMasterAccountRolesModal(true);
        break;
      case 'delete':
        setClientToRemove(client);
        setShowRemoveClientModal(true);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    if (role === 'Admin') {
      setSelectedLocations(['All Locations']);
    } else {
      setSelectedLocations([]);
    }
  };

  const handleAddClient = () => {
    if (selectedClientToAdd) {
      console.log('Adding client:', selectedClientToAdd);
      setShowAddClientModal(false);
      setSelectedClientToAdd('');
    }
  };

  const handleRemoveClient = () => {
    if (clientToRemove) {
      console.log('Removing client:', clientToRemove.companyName);
      setShowRemoveClientModal(false);
      setClientToRemove(null);
    }
  };

  const availableClients = [
    'Sunrise Donuts\' Donuts',
    'Metro Deli',
    'Fiesta Grill',
    'KFC',
    'Pizza Hut'
  ];

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title={`${vendorName} - Clients`} />
            <Button 
              variant="outline-secondary" 
              className={styles.backButton}
              onClick={() => navigate('/installer-vendors')}
            >
              ← Back to Installer Vendors
            </Button>
          </Card.Title>

          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Clients</h3>
              <Button 
                variant="primary" 
                className={styles.createClientButton}
                onClick={() => setShowAddClientModal(true)}
              >
                Add Client
              </Button>
            </div>

            <Table hover responsive className={styles.clientsTable}>
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Company Address</th>
                  <th>Company Number</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Master Account</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendorClients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.companyName}</td>
                    <td>{client.companyAddress}</td>
                    <td>{client.companyNumber}</td>
                    <td>
                      <span className={`${styles.roleBadge} ${styles[`role${client.clientRole}`]}`}>
                        {client.clientRole}
                      </span>
                    </td>
                    <td className={styles.permissionsCell}>
                      <div className={styles.permissionsList}>
                        {client.clientPermissions.slice(0, 2).map((permission, index) => (
                          <span key={index} className={styles.permissionTag}>
                            {permission}
                          </span>
                        ))}
                        {client.clientPermissions.length > 2 && (
                          <span className={styles.morePermissions}>
                            +{client.clientPermissions.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={styles.masterAccountCell}>
                      <div className={styles.masterAccountCount}>
                        <div className={styles.masterAccountPreview}>
                          <span>👥 {client.masterAccountUsers.length} Users</span>
                        </div>
                        <div className={styles.masterAccountPreview}>
                          <span>🔑 {client.masterAccountRoles.length} Roles</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className={styles.actionLink}
                          onClick={() => handleAction('manage-permissions', client)}
                        >
                          ⚙️ Manage Permissions
                        </Button>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className={styles.actionLink}
                          onClick={() => handleAction('view-master-account-users', client)}
                        >
                          👥 View Master Account Users
                        </Button>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className={styles.actionLink}
                          onClick={() => handleAction('view-master-account-roles', client)}
                        >
                          🔑 View Master Account Roles
                        </Button>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className={`${styles.actionLink} ${styles.deleteLink}`}
                          onClick={() => handleAction('delete', client)}
                        >
                          🗑️ Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {vendorClients.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center">
                      No clients found for this vendor
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Permissions Modal */}
      <Modal 
        show={showPermissionsModal} 
        onHide={() => setShowPermissionsModal(false)}
        size="lg"
        className={styles.permissionsModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Permissions - {selectedClient?.companyName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select 
                value={selectedRole} 
                onChange={(e) => handleRoleChange(e.target.value)}
              >
                <option value="">Select Role</option>
                <option value="Admin">Admin</option>
                <option value="Manager">Manager</option>
                <option value="User">User</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Permissions</Form.Label>
              <div className={styles.permissionsGrid}>
                {['View All', 'Edit All', 'Delete All', 'Manage Users', 'View Limited', 'Edit Limited'].map((permission) => (
                  <Form.Check
                    key={permission}
                    type="checkbox"
                    label={permission}
                    checked={selectedPermissions.includes(permission)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPermissions([...selectedPermissions, permission]);
                      } else {
                        setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
                      }
                    }}
                    className={styles.permissionCheckbox}
                  />
                ))}
              </div>
            </Form.Group>

            {selectedRole === 'Manager' && (
              <>
                <div className={styles.locationAssignmentHeader}>
                  <Form.Label>Assigned Locations</Form.Label>
                  <div className={styles.locationAssignmentButtons}>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => setSelectedLocations(['All Locations'])}
                    >
                      Select All
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => setSelectedLocations([])}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className={styles.locationsGrid}>
                  {['Harbor Foods Downtown', 'Harbor Foods Uptown', 'Harbor Foods Mall', 'Harbor Foods Airport'].map((location) => (
                    <Form.Check
                      key={location}
                      type="checkbox"
                      label={location}
                      checked={selectedLocations.includes(location)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLocations([...selectedLocations, location]);
                        } else {
                          setSelectedLocations(selectedLocations.filter(l => l !== location));
                        }
                      }}
                      className={styles.locationCheckbox}
                    />
                  ))}
                </div>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPermissionsModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" className={styles.saveButton}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Client Modal */}
      <Modal 
        show={showAddClientModal} 
        onHide={() => setShowAddClientModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Client to {vendorName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Client</Form.Label>
            <Form.Select 
              value={selectedClientToAdd} 
              onChange={(e) => setSelectedClientToAdd(e.target.value)}
            >
              <option value="">Choose a client...</option>
              {availableClients.map((client) => (
                <option key={client} value={client}>{client}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddClientModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddClient}>
            Add Client
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Remove Client Confirmation Modal */}
      <Modal show={showRemoveClientModal} onHide={() => setShowRemoveClientModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Remove Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove <strong>{clientToRemove?.companyName}</strong> from this vendor?</p>
          <p>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveClientModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRemoveClient}>
            Remove Client
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Master Account Users Modal */}
      <Modal 
        show={showMasterAccountUsersModal} 
        onHide={() => setShowMasterAccountUsersModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Master Account Users - {selectedClientForMasterAccount?.companyName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.masterAccountList}>
            {selectedClientForMasterAccount?.masterAccountUsers.map((user, index) => (
              <div key={index} className={styles.userPreview}>
                👤 {user}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMasterAccountUsersModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Master Account Roles Modal */}
      <Modal 
        show={showMasterAccountRolesModal} 
        onHide={() => setShowMasterAccountRolesModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Master Account Roles - {selectedClientForMasterAccount?.companyName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.masterAccountList}>
            {selectedClientForMasterAccount?.masterAccountRoles.map((role, index) => (
              <div key={index} className={styles.rolePreview}>
                🔑 {role}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMasterAccountRolesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default InstallerVendorClients;
